import styled, { css } from 'styled-components'

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 24px 0;
`

export const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px 0 20px;
  background: #eff3fa;
`

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px 20px 0;
`

export const Card = styled.div`
  background: #fff;
  border: 1px solid #dfe7f3;
  border-radius: 16px;
  box-shadow: 0 10px 24px rgba(32, 61, 84, 0.08);
  padding: 24px;
`

export const UploadBox = styled.label<{ $isDragging?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  border: 2px dashed
    ${({ $isDragging }) => ($isDragging ? '#c6016b' : '#2d749c')};
  border-radius: 16px;
  padding: 28px;
  cursor: pointer;
  background: linear-gradient(
    180deg,
    rgba(45, 116, 156, ${({ $isDragging }) => ($isDragging ? 0.14 : 0.08)}) 0%,
    rgba(198, 1, 107, ${({ $isDragging }) => ($isDragging ? 0.08 : 0.03)}) 100%
  );
  transition: border-color 0.2s ease, background 0.2s ease;
`

export const FileInput = styled.input`
  display: none;
`

export const TestHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`

export const StatusPill = styled.span<{ $tone: 'success' | 'warning' | 'error' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 700;

  ${({ $tone }) =>
    $tone === 'success'
      ? css`
          background: #e8f7ef;
          color: #147a45;
        `
      : $tone === 'warning'
      ? css`
          background: #fff4df;
          color: #8f5a00;
        `
      : css`
          background: #fee9e9;
          color: #b3261e;
        `}
`

export const EditorGrid = styled.div<{ $columns?: string }>`
  display: grid;
  grid-template-columns: ${({ $columns }) =>
    $columns || 'repeat(2, minmax(0, 1fr))'};
  gap: 16px;
  margin-top: 16px;
  align-items: end;

  > * {
    min-width: 0;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

export const EditorLabel = styled.span`
  display: block;
  margin-bottom: 8px;
  color: #476075;
  font-size: 13px;
  font-weight: 700;
`

export const RoleSummary = styled.div`
  min-height: 54px;
  border: 1px solid #dfe7f3;
  border-radius: 12px;
  background: #f8fbff;
  padding: 14px 16px;
  color: #1f2a37;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
`

export const MutedText = styled.p`
  margin: 6px 0 0;
  color: #6b7b8c;
  font-size: 14px;
  line-height: 1.5;
`

export const RecommendationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-top: 18px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

export const RecommendationCard = styled.div<{
  $active?: boolean
  $roleTone?: 'dominante' | 'auxiliar_1' | 'auxiliar_2' | 'menos_eficiente'
}>`
  border: 1px solid
    ${({ $active, $roleTone }) => {
      if (!$active) return '#dfe7f3'
      if ($roleTone === 'dominante') return '#c6016b'
      if ($roleTone === 'menos_eficiente') return '#7a8a9a'
      if ($roleTone === 'auxiliar_1') return '#2d749c'
      if ($roleTone === 'auxiliar_2') return '#5f8fb0'
      return '#2d749c'
    }};
  border-radius: 16px;
  padding: 18px;
  background: ${({ $active, $roleTone }) => {
    if (!$active) return '#fff'
    if ($roleTone === 'dominante') {
      return 'linear-gradient(180deg, rgba(198, 1, 107, 0.12) 0%, rgba(198, 1, 107, 0.03) 100%)'
    }
    if ($roleTone === 'menos_eficiente') {
      return 'linear-gradient(180deg, rgba(122, 138, 154, 0.12) 0%, rgba(122, 138, 154, 0.03) 100%)'
    }
    if ($roleTone === 'auxiliar_1') {
      return 'linear-gradient(180deg, rgba(45, 116, 156, 0.12) 0%, rgba(45, 116, 156, 0.03) 100%)'
    }
    if ($roleTone === 'auxiliar_2') {
      return 'linear-gradient(180deg, rgba(95, 143, 176, 0.12) 0%, rgba(95, 143, 176, 0.03) 100%)'
    }
    return 'linear-gradient(180deg, rgba(45, 116, 156, 0.08) 0%, rgba(198, 1, 107, 0.03) 100%)'
  }};
  box-shadow: ${({ $roleTone }) =>
    $roleTone === 'dominante'
      ? '0 14px 28px rgba(198, 1, 107, 0.14)'
      : 'none'};
  transform: ${({ $roleTone }) =>
    $roleTone === 'dominante' ? 'translateY(-2px)' : 'none'};
  transition:
    border-color 0.2s ease,
    background 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
`

export const QuadrantHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`

export const QuadrantName = styled.strong`
  font-size: 28px;
  line-height: 1;
  letter-spacing: -0.03em;
`

export const QuadrantBadge = styled.span<{
  $roleTone: 'dominante' | 'auxiliar_1' | 'auxiliar_2' | 'menos_eficiente'
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  ${({ $roleTone }) =>
    $roleTone === 'dominante'
      ? css`
          background: #fde7f2;
          color: #a10058;
        `
      : $roleTone === 'menos_eficiente'
      ? css`
          background: #eef2f6;
          color: #586575;
        `
      : $roleTone === 'auxiliar_1'
      ? css`
          background: #e7f2f8;
          color: #1f6388;
        `
      : css`
          background: #edf4f8;
          color: #4f7f9f;
        `}
`

export const ToggleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`

export const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin-top: 4px;
  accent-color: #c6016b;
`

export const Select = styled.select`
  width: 100%;
  min-width: 0;
  height: 42px;
  border: 1px solid #d7dee8;
  border-radius: 10px;
  background: #fff;
  padding: 0 12px;
  box-sizing: border-box;
  color: #1f2a37;
  font-size: 14px;
`

export const Input = styled.input`
  width: 100%;
  min-width: 0;
  height: 42px;
  border: 1px solid #d7dee8;
  border-radius: 10px;
  background: #fff;
  padding: 0 12px;
  box-sizing: border-box;
  color: #1f2a37;
  font-size: 14px;
`

export const TextArea = styled.textarea`
  width: 100%;
  min-height: 132px;
  border: 1px solid #d7dee8;
  border-radius: 12px;
  background: #fff;
  padding: 14px 16px;
  color: #1f2a37;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
`

export const InfoBox = styled.div`
  width: 100%;
  min-height: 132px;
  border: 1px solid #d7dee8;
  border-radius: 12px;
  background: #f8fbff;
  padding: 14px 16px;
  color: #1f2a37;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
`

export const TableWrap = styled.div`
  margin-top: 16px;
  overflow-x: auto;
  border: 1px solid #e3e8ef;
  border-radius: 14px;
`

export const TableToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 18px;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
  }
`

export const CounterPill = styled.span<{ $tone?: 'default' | 'warning' }>`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 700;
  background: ${({ $tone }) =>
    $tone === 'warning' ? '#fff4df' : '#edf4f8'};
  color: ${({ $tone }) =>
    $tone === 'warning' ? '#8f5a00' : '#2d749c'};
`

export const InlineActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 900px) {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }
`

export const SearchableSelectWrap = styled.div`
  position: relative;
  min-width: 320px;
  flex: 1;

  @media (max-width: 900px) {
    min-width: 0;
    width: 100%;
  }
`

export const SearchResults = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid #d7dee8;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 16px 32px rgba(32, 61, 84, 0.16);
`

export const SearchResultButton = styled.button<{ $active?: boolean }>`
  display: block;
  width: 100%;
  border: none;
  border-bottom: 1px solid #edf1f6;
  background: ${({ $active }) => ($active ? '#fce8f3' : '#fff')};
  color: #1f2a37;
  padding: 11px 12px;
  text-align: left;
  font-size: 14px;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8fbff;
  }
`

export const SearchEmptyState = styled.div`
  padding: 12px;
  color: #6b7b8c;
  font-size: 13px;
`

export const SmallButton = styled.button`
  height: 42px;
  border: 1px solid #c6016b;
  border-radius: 10px;
  background: #fff;
  color: #c6016b;
  padding: 0 14px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
`

export const RemoveButton = styled.button`
  height: 36px;
  border: 1px solid #e3cad8;
  border-radius: 10px;
  background: #fff7fb;
  color: #a10058;
  padding: 0 12px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 14px 16px;
    border-bottom: 1px solid #edf1f6;
    text-align: left;
    vertical-align: middle;
    font-size: 14px;
  }

  th {
    background: #f7f9fc;
    color: #476075;
    font-weight: 700;
  }

  tr:last-child td {
    border-bottom: none;
  }
`

export const ValidationBox = styled.div<{ $tone: 'warning' | 'error' }>`
  margin-top: 14px;
  border-radius: 14px;
  padding: 16px 18px;
  border: 1px solid
    ${({ $tone }) => ($tone === 'error' ? '#f2c7c5' : '#f5d59b')};
  background: ${({ $tone }) =>
    $tone === 'error' ? '#fff4f4' : '#fff8e9'};
  color: #344054;

  ul {
    margin: 10px 0 0;
    padding-left: 18px;
  }
`

export const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`

export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 6px 12px;
  background: #eff3fa;
  color: #2d749c;
  font-size: 14px;
  font-weight: 600;
`

export const SectionDivider = styled.div`
  height: 1px;
  background: transparent;
`

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
`
